"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import { Logo } from "@/design-elements/logo";
import SearchModal from "../search/SearchModal";
import CitySelector from "./CitySelector";
import UserSelector from "./UserSelector";
import EligibilityCapsPopup from "./EligibilityCapsPopup";
import Toggle from "@/components/common/Toggle";
import { useAppData } from "@/store/appStore";
import { useScrollHidden } from "@/hooks/useScrollHidden";
import {
	PRODUCT_NAME_CAR,
	PRODUCT_NAME_DEVICES,
} from "@/constants/productTypes";

function BackButton() {
	const router = useRouter();
	return (
		<button
			type="button"
			onClick={() => router.back()}
			aria-label="Go back"
			className="flex items-center justify-center w-[52px] h-[52px] rounded-[10px] bg-[var(--colors-automint-neutral-800)] flex-shrink-0"
		>
			<ChevronLeft className="w-6 h-6 text-automint-neon" />
		</button>
	);
}

const Header = ({
	initialCapsData,
}: {
	initialCapsData?: EligibilityCapsResponse | null;
}) => {
	const [appData] = useAppData();
	const pathname = usePathname();

	const isLoggedIn = appData?.user && appData?.isUserReady;
	const isCarContext = appData?.onboardingCompletionType === PRODUCT_NAME_CAR;

	// Mobile header variants — based on current route
	const isHome = pathname === "/";
	const isProductListing = pathname === "/product";
	const isProductDetail =
		/^\/product\/[^/]+(?:\/[^/]+)?\/?$/.test(pathname || "") &&
		pathname !== "/product";

	// "full" = logo + toggle + eligibility + search (home and /product)
	// "slim" = back button + search (product detail pages)
	// "back" = just back button (everywhere else)
	const mobileVariant: "full" | "slim" | "back" =
		isHome || isProductListing ? "full" : isProductDetail ? "slim" : "back";

	const showToggleOnPage =
		(isHome || isProductListing) && appData?.showToggle === true;

	const isHidden = useScrollHidden(false);

	if (pathname?.startsWith("/onboarding") || pathname?.startsWith("/game"))
		return null;

	const visibilityClass = isHidden ? "-translate-y-full" : "translate-y-0";

	return (
		<div
			data-mobile-variant={mobileVariant}
			className={`block md:flex w-full md:h-full bg-automint-black shadow-sm ${visibilityClass}`}
		>
			<div className="flex flex-col md:flex-row md:items-center md:h-full w-full max-w-7xl mx-auto md:px-6 lg:px-8">
				{/* Desktop header — unchanged */}
				<div className="hidden md:flex md:flex-1 md:items-center md:h-full">
					<div className="flex items-center justify-between md:p-0 md:gap-0 md:h-full md:flex-1 md:flex-row">
						{/* Left — Logo + Toggle pill */}
						<div
							className={`flex items-center gap-1 h-[52px] px-1 flex-shrink-0 ${showToggleOnPage ? "bg-[var(--colors-automint-neutral-800)] rounded-[10px]" : ""}`}
						>
							<Link
								id="header_logo"
								href="/"
								aria-label="Home"
								className={`flex items-center justify-center h-[42px] flex-shrink-0 ${showToggleOnPage ? "w-[42px]" : "px-2"}`}
							>
								<Logo
									showText={!showToggleOnPage}
									size={42}
									invert
									className="[&_svg]:!w-[42px] [&_svg]:!h-[42px]"
								/>
							</Link>
							{showToggleOnPage && (
								<Toggle
									tabs={[PRODUCT_NAME_CAR, PRODUCT_NAME_DEVICES]}
									page="homepage"
									bare
								/>
							)}
						</div>

						{/* Desktop search — middle of top row */}
						<div className="flex flex-1 h-full justify-center items-center mx-4">
							<div className="w-full max-w-md">
								<SearchModal type="header" />
							</div>
						</div>

						{/* Right — Eligibility, City, Login/Profile */}
						<div className="flex items-center gap-3 flex-shrink-0">
							<EligibilityCapsPopup initialData={initialCapsData} />
							{isCarContext && (
								<div
									id="header_location"
									className="flex items-center h-[42px]"
								>
									<CitySelector type="header" />
								</div>
							)}
							{isLoggedIn ? (
								<div id="header_profile">
									<UserSelector />
								</div>
							) : (
								<div id="header_login">
									<Link
										href={`/authenticate/email?next=${encodeURIComponent(pathname || "/")}`}
										className="flex justify-center items-center gap-2 px-3 h-[42px] rounded-[10px] bg-automint-black border-[1.5px] border-automint-neon"
									>
										<span className="text-automint-neon text-sm font-medium">
											Login
										</span>
									</Link>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* MOBILE header — variant-based */}
				<div className="md:hidden w-full">
					{mobileVariant === "full" && (
						<>
							<div className="flex items-center justify-between px-4 py-2">
								{/* Left — Logo + Toggle pill */}
								<div
									className={`flex items-center gap-1 h-[42px] px-1 flex-shrink-0 ${showToggleOnPage ? "bg-[var(--colors-automint-neutral-800)] rounded-[10px]" : ""}`}
								>
									<Link
										id="header_logo"
										href="/"
										aria-label="Home"
										className={`flex items-center justify-center h-[36px] flex-shrink-0 ${showToggleOnPage ? "w-[36px]" : "px-2"}`}
									>
										<Logo
											showText={!showToggleOnPage}
											size={42}
											invert
											className="[&_svg]:!w-[36px] [&_svg]:!h-[36px]"
										/>
									</Link>
									{showToggleOnPage && (
										<Toggle
											tabs={[PRODUCT_NAME_CAR, PRODUCT_NAME_DEVICES]}
											page="homepage"
											bare
										/>
									)}
								</div>

								{/* Right — Eligibility only on mobile (login lives in bottom nav) */}
								<div className="flex items-center gap-2 flex-shrink-0">
									<EligibilityCapsPopup initialData={initialCapsData} />
								</div>
							</div>
							{/* Search bar row */}
							<div className="px-4 pb-3">
								<SearchModal type="header" />
							</div>
						</>
					)}

					{mobileVariant === "slim" && (
						<div className="flex items-center gap-2 px-4 h-[65px]">
							<BackButton />
							<div className="flex-1 min-w-0">
								<SearchModal type="header" />
							</div>
						</div>
					)}

					{mobileVariant === "back" && (
						<div className="flex items-center px-4 h-[65px]">
							<BackButton />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Header;
